import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Package, LogOut, ShieldCheck, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const TRACK_STEPS = ['paid', 'processing', 'shipped', 'delivered'];
const STATUS_LABELS = { created: 'Payment Pending', paid: 'Paid', processing: 'Processing', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled' };
const STATUS_COLORS = {
  created: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const StatusTracker = ({ status }) => {
  if (status === 'created' || status === 'cancelled') return null;
  const currentIdx = TRACK_STEPS.indexOf(status);
  return (
    <div className="flex items-center mt-4" data-testid="order-status-tracker">
      {TRACK_STEPS.map((step, i) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= currentIdx ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
              {i <= currentIdx ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-[11px] mt-1 ${i <= currentIdx ? 'text-emerald-700 font-medium' : 'text-gray-400'}`}>{STATUS_LABELS[step]}</span>
          </div>
          {i < TRACK_STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 mb-4 ${i < currentIdx ? 'bg-emerald-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const OrderCard = ({ order }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid={`order-card-${order.order_id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div>
        <p className="font-semibold text-gray-900">Order {order.order_id}</p>
        <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
      </div>
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`} data-testid="order-status-badge">
        {STATUS_LABELS[order.status] || order.status}
      </span>
    </div>
    <div className="space-y-2 mb-3">
      {order.items?.map((item, i) => (
        <div key={i} className="flex justify-between text-sm">
          <span className="text-gray-700">{item.name} × {item.quantity}</span>
          <span className="text-gray-900 font-medium">₹{(item.price * item.quantity).toLocaleString('en-IN')}</span>
        </div>
      ))}
    </div>
    <div className="flex justify-between border-t border-gray-100 pt-3">
      <span className="font-semibold text-gray-900">Total</span>
      <span className="font-bold text-emerald-600">₹{order.amount?.toLocaleString('en-IN')}</span>
    </div>
    <StatusTracker status={order.status} />
  </div>
);

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === null) navigate('/login', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/api/orders/my`, { withCredentials: true })
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]));
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Account | Harpalyk</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="pt-20 min-h-screen bg-gray-50" data-testid="account-page">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-lg">
                  {(user.name || user.email)[0].toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-bold text-gray-900" data-testid="account-user-name">{user.name || 'Customer'}</p>
                <p className="text-sm text-gray-500" data-testid="account-user-email">{user.email}</p>
              </div>
            </div>
            <div className="flex gap-3">
              {user.role === 'admin' && (
                <Link to="/admin" data-testid="admin-dashboard-link" className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all">
                  <ShieldCheck className="w-4 h-4" /> Admin Dashboard
                </Link>
              )}
              <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-600" /> Order History
          </h2>

          {orders === null ? (
            <p className="text-gray-500">Loading orders…</p>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center" data-testid="no-orders">
              <p className="text-gray-600 mb-4">You haven't placed any orders yet.</p>
              <Link to="/#products" className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold transition-all">
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((order) => <OrderCard key={order.order_id} order={order} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
